import { useCallback, useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  Archive,
  ArrowLeft,
  Bot,
  CheckCheck,
  Download,
  Eye,
  FileText,
  FilePlus2,
  Copy,
  Inbox,
  Loader2,
  Paperclip,
  Play,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ORPCError } from '@orpc/client'
import {
  attachmentPreview,
  ruleMovesToProcessed,
  type InboxEmail,
  type InboxFolder,
} from '@hub/contracts'
import { orpc } from '@/lib/orpc-client'
import { useDateLocale } from '@/i18n/dates'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { EmailStatusPill, RunStatusPill } from './status-pill'
import { HUB_BUBBLE_ASK_EVENT, type HubBubbleAskDetail } from '@/components/chat/hub-bubble'
import { SelectionActionMenu } from '@/components/chat/selection-menu'
import { ImageLightbox } from '@/components/chat/image-lightbox'
import { AttachmentThumbnail, attachmentUrl, PdfAttachmentDialog } from './attachment-preview'
import { EmailHtmlBody } from './email-html-body'
import { EmailTextBody } from './email-text-body'

type Detail = Awaited<ReturnType<typeof orpc.inbox.getEmail>>

export interface EmailDetailProps {
  projectId: string
  emailId: string
  /** List row, shown while the full email loads. */
  summary: InboxEmail | null
  onBack: () => void
  onMoved: (folder: InboxFolder) => void
  /** The email is gone — drop the selection (inbox-page reloads the list). */
  onDeleted: () => void
  onChanged: () => void
}

/** Opens the email's HUB bubble (inbox-page.tsx mounts it for the selected email). */
function askHub(detail: HubBubbleAskDetail = {}) {
  window.dispatchEvent(new CustomEvent<HubBubbleAskDetail>(HUB_BUBBLE_ASK_EVENT, { detail }))
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function EmailDetail({
  projectId,
  emailId,
  summary,
  onBack,
  onMoved,
  onDeleted,
  onChanged,
}: EmailDetailProps) {
  const { t } = useTranslation('inbox')
  const { intl } = useDateLocale()
  const [detail, setDetail] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  /** The attachment open in the lightbox / PDF viewer. */
  const [viewing, setViewing] = useState<{ index: number; kind: 'image' | 'pdf' } | null>(null)
  /** HTML when the mail has it (D8); the text body stays one click away. */
  const [bodyView, setBodyView] = useState<'html' | 'text'>('html')
  /** Attachments the HTML view shows as pictures in the mail. */
  const [inlineAttachments, setInlineAttachments] = useState<number[]>([])
  const [frameDocument, setFrameDocument] = useState<Document | null>(null)

  const load = useCallback(async () => {
    try {
      setDetail(await orpc.inbox.getEmail({ projectId, emailId }))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [projectId, emailId])

  useEffect(() => {
    void load()
  }, [load])

  // A preview belongs to the email it was opened on.
  useEffect(() => setViewing(null), [emailId])

  // Runs and status change while the agent works — the list's `inbox:changed`
  // reload calls onChanged; we piggyback on the same signal.
  useEffect(() => {
    if (!summary) return
    void load()
  }, [summary?.status, summary?.threadId, summary?.folder]) // eslint-disable-line react-hooks/exhaustive-deps

  const email = detail?.email ?? summary
  const runs = detail?.runs ?? []
  const rules = detail?.rules.filter((r) => r.enabled) ?? []

  const withBusy = async (key: string, fn: () => Promise<void>) => {
    setBusy(key)
    try {
      await fn()
    } finally {
      setBusy(null)
    }
  }

  const reportRunError = (err: unknown) => {
    if (err instanceof ORPCError && err.code === 'FORBIDDEN') toast.error(t('credits.exhausted'))
    else toast.error(err instanceof Error ? err.message : String(err))
  }

  const move = (folder: InboxFolder) =>
    withBusy(`move:${folder}`, async () => {
      await orpc.inbox.moveEmail({ projectId, emailId, folder })
      onMoved(folder)
      onChanged()
    })

  // Board 63qj: a hard delete behind a real dialog (never `confirm()`).
  const remove = () =>
    withBusy('delete', async () => {
      try {
        await orpc.inbox.deleteEmail({ projectId, emailId })
        toast.success(t('delete.done'))
        setDeleteOpen(false)
        onDeleted()
        onChanged()
      } catch (err) {
        if (err instanceof ORPCError && err.code === 'CONFLICT') toast.error(t('delete.processing'))
        else toast.error(err instanceof Error ? err.message : t('delete.failed'))
      }
    })

  const runRule = (ruleId: string) =>
    withBusy(`rule:${ruleId}`, async () => {
      try {
        await orpc.inbox.runRule({ projectId, emailId, ruleId })
        toast.success(t('ask.started'))
        onChanged()
        await load()
      } catch (err) {
        reportRunError(err)
      }
    })

  const saveAttachment = (index: number) =>
    withBusy(`save:${index}`, async () => {
      try {
        const saved = await orpc.inbox.saveAttachment({
          projectId,
          emailId,
          attachmentIndex: index,
        })
        toast.success(
          t(saved.alreadySaved ? 'detail.alreadySaved' : 'detail.saved', { title: saved.title })
        )
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('detail.saveFailed'))
      }
    })

  if (!email) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {error ?? t('loading')}
      </div>
    )
  }

  const viewingAttachment = viewing
    ? email.attachments.find((a) => a.index === viewing.index)
    : undefined
  const from = email.fromName ? `${email.fromName} <${email.fromAddress}>` : email.fromAddress
  const body = detail?.email.textBody ?? null
  const html = detail?.email.body.html ?? null
  const showHtml = html !== null && bodyView === 'html'
  // Only while the HTML view shows them; the text view has no other way to them.
  const chips = showHtml
    ? email.attachments.filter((a) => !inlineAttachments.includes(a.index))
    : email.attachments
  const copyFrom = async () => {
    try {
      await navigator.clipboard.writeText(email.fromAddress)
      toast.success(t('detail.copiedFrom'))
    } catch {
      // clipboard unavailable — the address is visible to select by hand
    }
  }
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Toolbar */}
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-wrap items-center gap-1 border-b px-3 py-2">
          <Button variant="ghost" size="sm" className="h-8 px-2 md:hidden" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <DropdownMenu modal={false}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    aria-label={t('actions.runRule')}
                    disabled={email.status === 'processing'}
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('actions.runRule')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="w-56">
              {rules.length === 0 ? (
                <DropdownMenuLabel className="font-normal text-muted-foreground">
                  {t('actions.noRules')}
                </DropdownMenuLabel>
              ) : (
                <>
                  {rules.map((rule) => (
                    <DropdownMenuItem
                      key={rule.id}
                      onClick={() => void runRule(rule.id)}
                      disabled={busy !== null}
                      className="gap-2"
                    >
                      <span className="min-w-0 flex-1 truncate">{rule.name}</span>
                      {ruleMovesToProcessed(rule) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="text-muted-foreground shrink-0"
                              aria-label={t('actions.runRuleMoves')}
                            >
                              <CheckCheck className="h-3.5 w-3.5" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right">{t('actions.runRuleMoves')}</TooltipContent>
                        </Tooltip>
                      )}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/$projectId/inbox/rules" params={{ projectId }}>
                  {t('actions.manageRules')}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="ml-auto flex items-center gap-1.5">
            {email.folder !== 'processed' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5"
                disabled={busy !== null}
                onClick={() => void move('processed')}
              >
                <CheckCheck className="h-4 w-4" />
                <span className="hidden lg:inline">{t('actions.markProcessed')}</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5"
              disabled={busy !== null}
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden lg:inline">{t('actions.delete')}</span>
            </Button>
            {email.folder !== 'archive' ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5"
                disabled={busy !== null}
                onClick={() => void move('archive')}
              >
                <Archive className="h-4 w-4" />
                <span className="hidden lg:inline">{t('actions.archive')}</span>
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5"
                disabled={busy !== null}
                onClick={() => void move('inbox')}
              >
                <Inbox className="h-4 w-4" />
                <span className="hidden lg:inline">{t('actions.moveToInbox')}</span>
              </Button>
            )}
          </div>
        </div>
      </TooltipProvider>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-6 px-6 py-5">
          <header className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-xl font-semibold leading-tight">
                {email.subject || t('noSubject')}
              </h2>
              <EmailStatusPill status={email.status} className="mt-1" />
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
              <dt className="text-muted-foreground">{t('detail.from')}</dt>
              <dd className="flex min-w-0 items-center gap-1">
                <span className="truncate">{from}</span>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => void copyFrom()}
                        aria-label={t('detail.copyFrom')}
                        className="text-muted-foreground hover:text-foreground shrink-0 rounded p-0.5"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{t('detail.copyFrom')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </dd>
              <dt className="text-muted-foreground">{t('detail.to')}</dt>
              <dd className="truncate">{email.toAddresses.join(', ')}</dd>
              {email.ccAddresses.length > 0 && (
                <>
                  <dt className="text-muted-foreground">{t('detail.cc')}</dt>
                  <dd className="truncate">{email.ccAddresses.join(', ')}</dd>
                </>
              )}
              <dt className="text-muted-foreground">{t('detail.received')}</dt>
              <dd>
                {new Date(email.receivedAt).toLocaleString(intl, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </dd>
            </dl>
            {email.errorMessage && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <span className="font-medium">{t('detail.error')}:</span> {email.errorMessage}
              </p>
            )}
          </header>

          {chips.length > 0 && (
            <section className="space-y-2">
              <h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Paperclip className="h-3.5 w-3.5" />
                {t('detail.attachments')}
              </h3>
              <ul className="flex flex-wrap gap-2">
                {chips.map((a) => {
                  const preview = attachmentPreview(a)
                  const open = preview
                    ? () => setViewing({ index: a.index, kind: preview.kind })
                    : undefined
                  return (
                    <li
                      key={a.index}
                      data-slot="email-attachment"
                      className={cn(
                        'flex max-w-full min-w-0 items-center gap-1 rounded-md border bg-muted/30 py-1 pr-1 text-sm',
                        preview?.kind === 'image' ? 'pl-1' : 'pl-3'
                      )}
                    >
                      {preview?.kind === 'image' && open && (
                        <AttachmentThumbnail
                          src={attachmentUrl(projectId, emailId, a.index, true)}
                          filename={a.filename}
                          onOpen={open}
                          className="mr-1"
                        />
                      )}
                      {open ? (
                        <button
                          type="button"
                          onClick={open}
                          title={a.filename}
                          className="min-w-0 max-w-[220px] truncate text-left hover:underline"
                        >
                          {a.filename}
                        </button>
                      ) : (
                        <span className="min-w-0 max-w-[220px] truncate" title={a.filename}>
                          {a.filename}
                        </span>
                      )}
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {formatBytes(a.size)}
                      </span>
                      {preview?.kind === 'pdf' && open && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          title={t('detail.openPdf')}
                          aria-label={t('detail.openPdf')}
                          onClick={open}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button asChild variant="ghost" size="sm" className="h-7 px-2">
                        <a
                          href={attachmentUrl(projectId, emailId, a.index)}
                          download={a.filename}
                          title={t('detail.download')}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                      {a.documentId ? (
                        <Button asChild variant="ghost" size="sm" className="h-7 px-2">
                          <Link
                            to="/$projectId/write/$docId"
                            params={{ projectId, docId: a.documentId }}
                            title={t('detail.openDocument')}
                            aria-label={t('detail.openDocument')}
                          >
                            <FileText className="h-3.5 w-3.5 text-emerald-600" />
                          </Link>
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          title={t('detail.saveToDocuments')}
                          aria-label={t('detail.saveToDocuments')}
                          disabled={busy !== null}
                          onClick={() => void saveAttachment(a.index)}
                        >
                          {busy === `save:${a.index}` ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <FilePlus2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          )}

          <section>
            {body === null ? (
              <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : (
              <>
                {html !== null && (
                  <button
                    type="button"
                    data-slot="email-view-switch"
                    onClick={() => setBodyView(showHtml ? 'text' : 'html')}
                    className="text-muted-foreground hover:text-foreground mb-2 min-h-6 text-xs underline underline-offset-2"
                  >
                    {t(showHtml ? 'body.showText' : 'body.showHtml')}
                  </button>
                )}
                {detail?.email.body.htmlTooLarge && (
                  <p className="text-muted-foreground mb-2 text-xs">{t('body.htmlTooLarge')}</p>
                )}
                {showHtml ? (
                  <EmailHtmlBody
                    html={html}
                    inlineImages={detail?.email.body.inlineImages ?? []}
                    projectId={projectId}
                    emailId={emailId}
                    subject={email.subject}
                    onInlineAttachments={setInlineAttachments}
                    onFrameDocument={setFrameDocument}
                  />
                ) : (
                  <EmailTextBody text={body} subject={email.subject} />
                )}
                {/* Select text in the mail → "Ask HUB": the quote lands in the
                    bubble's composer once its thread exists. */}
                <SelectionActionMenu
                  draftKey={{ projectId, threadKey: emailId }}
                  selector='[data-slot="inbox-email-body"]'
                  label={t('detail.askAboutSelection')}
                  onPick={(selection) => askHub({ selection })}
                  frameDocument={showHtml ? frameDocument : null}
                />
              </>
            )}
          </section>

          {runs.length > 0 && (
            <section className="space-y-2 border-t pt-4">
              <h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Bot className="h-3.5 w-3.5" />
                {t('runs.title')}
              </h3>
              <ul className="space-y-2">
                {runs.map((run) => (
                  <li key={run.id}>
                    <button
                      type="button"
                      onClick={() =>
                        askHub(
                          run.assistantMessageId
                            ? { scrollToMessageId: run.assistantMessageId }
                            : {}
                        )
                      }
                      title={t('runs.openReply')}
                      className="w-full rounded-md border px-3 py-2 text-left text-sm transition-colors hover:border-foreground/20 hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate font-medium">
                          {run.ruleName ? t('runs.rule', { name: run.ruleName }) : t('runs.manual')}
                        </span>
                        <RunStatusPill status={run.status} />
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {new Date(run.createdAt).toLocaleString(intl, {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                        {run.prompt}
                      </p>
                      {run.errorMessage && (
                        <p className="mt-1 text-xs text-destructive">{run.errorMessage}</p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      {viewingAttachment && viewing?.kind === 'image' && (
        <ImageLightbox
          image={{
            url: attachmentUrl(projectId, emailId, viewingAttachment.index, true),
            filename: viewingAttachment.filename,
            size: viewingAttachment.size,
          }}
          open
          onOpenChange={(next) => !next && setViewing(null)}
        />
      )}
      {viewingAttachment && viewing?.kind === 'pdf' && (
        <PdfAttachmentDialog
          url={attachmentUrl(projectId, emailId, viewingAttachment.index, true)}
          filename={viewingAttachment.filename}
          open
          onOpenChange={(next) => !next && setViewing(null)}
        />
      )}

      <DeleteEmailDialog
        open={deleteOpen}
        busy={busy === 'delete'}
        onOpenChange={setDeleteOpen}
        onConfirm={() => void remove()}
      />
    </div>
  )
}

/**
 * "Delete the email?" — a real alert dialog (board 63qj) that says what goes
 * (the mail, its attachments, HUB's chat) and what stays (saved documents);
 * the destructive action is red and `busy` keeps it open while the request
 * runs. Radix would close on Action by default; the caller closes it once
 * the delete succeeded.
 */
function DeleteEmailDialog({
  open,
  busy,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  busy: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('inbox')
  return (
    <AlertDialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <AlertDialogContent data-testid="delete-email-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('delete.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t('delete.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={busy}
            onClick={(event) => {
              event.preventDefault()
              onConfirm()
            }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {t('delete.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
